import { Input } from 'antd';
import DropdownMenu from 'components/common/dropdown-menu';
import type { InputProps, InputRef, MenuProps } from 'antd';
import withTooltip from 'components/common/form-controlled/with-tooltip.hoc';
import { getCommonInputCssFromProps } from 'components/common/input-components/common-styles';
import { ErrorMessage } from 'components/common/form-components';
import React from 'react';
import isString from 'lodash/isString';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled';
import withLabel from 'components/common/form-controlled/with-label.hoc';
import { InputAddonGroup, hasAddon, type InputAddonProps } from 'components/common/input-components/input-addons';

// The addon props are ours, not antd's deprecated ones: the wrapper draws addons itself.
export interface TextInputProps extends Omit<InputProps, 'addonBefore' | 'addonAfter'>, InputAddonProps {
  id?: string;
  name?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  placeholderValues?: Record<string, string>;
  disabled?: boolean;
  invalid?: boolean;
  error?: InputError;
  hideError?: boolean;
  hideEmptyError?: boolean;
  errorShouldNotTakeSpace?: boolean;
  'data-e2e': string;
  fullWidth?: boolean;
  grow?: boolean;
  menu?: MenuProps;
  ref?: React.Ref<InputRef>;
}

/**
 * Password has no wrapper of its own and needs none: it renders `.ant-input-affix-wrapper`, which the antd
 * Input block in `antd.constants` already restyles. Exported so a call site outside `components/common` has
 * an import other than antd's.
 */
export const PasswordInput = Input.Password;

export type InputError = string | InputErrorExtended | null;
type InputErrorExtended = { id: string; values: Record<string, any> };

const TextInput = React.forwardRef<InputRef, TextInputProps>((props, ref) => {
  const {
    placeholder,
    placeholderValues,
    error,
    hideError,
    hideEmptyError,
    fullWidth,
    grow,
    menu,
    errorShouldNotTakeSpace,
    addonBefore,
    addonAfter,
    addonColor,
    className,
    style,
    ...otherProps
  } = props as TextInputProps;
  const intl = useIntl();
  const translatedPlaceholder = placeholder && intl.formatMessage({ id: placeholder }, placeholderValues);

  const errorAsObj = error as InputErrorExtended;
  const errorId = !isString(error) ? errorAsObj?.id : error;
  const errorValues = errorAsObj?.values;

  const withAddon = hasAddon({ addonBefore, addonAfter });

  const field = (
    <StyledInput
      // withController passes the form's error message as `invalid`, so coerce it to a valid token.
      aria-invalid={otherProps.invalid || errorId ? true : undefined}
      {...otherProps}
      invalid={otherProps.invalid || Boolean(errorId)}
      {...(withAddon ? {} : { className, style })}
      ref={ref}
      placeholder={translatedPlaceholder}
      autoComplete={menu ? 'off' : otherProps.autoComplete}
    />
  );

  const input = withAddon ? (
    <InputAddonGroup
      addonBefore={addonBefore}
      addonAfter={addonAfter}
      addonColor={addonColor}
      disabled={otherProps.disabled}
      invalid={Boolean(otherProps.invalid || errorId)}
      className={className}
      style={style}>
      {field}
    </InputAddonGroup>
  ) : (
    field
  );

  return (
    <FlexColumn fullWidth={fullWidth} grow={grow}>
      {menu ? (
        <DropdownMenu disabled={otherProps.disabled} menu={menu}>
          <span>{input}</span>
        </DropdownMenu>
      ) : (
        input
      )}
      {!hideError && (
        <ErrorMessage
          data-e2e={otherProps['data-e2e'] ? `${otherProps['data-e2e']}-error` : null}
          errorShouldNotTakeSpace={errorShouldNotTakeSpace}
          hideWhenEmpty={hideEmptyError}>
          {errorId && <FormattedMessage id={errorId} values={errorValues} />}
        </ErrorMessage>
      )}
    </FlexColumn>
  );
});

TextInput.displayName = 'TextInput';

// Sizing and look come from the antd Input block in `antd.constants`. `invalid` only styles the field:
// antd would hand it to the input element, which React warns about.
const StyledInput = styled(Input, {
  shouldForwardProp: prop => prop !== 'invalid'
})<TextInputProps>`
  ${({ invalid, error, theme }) => getCommonInputCssFromProps({ theme, invalid: invalid || Boolean(error) })}
`;

const FlexColumn = styled.div<{ fullWidth?: boolean; grow?: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ fullWidth }) => (fullWidth ? 'width: 100%;' : '')}
  ${({ grow }) => (grow ? 'flex-grow: 1' : '')}
`;

export const TextInputWithLabel = withLabel(TextInput);
export const TextInputWithTooltipAndLabel = withLabel(withTooltip(TextInput));
export default TextInput;
